<script setup>
/**
 * Theme-aware icon renderer.
 *
 * Renders the Vue icon components generated from the SVG assets
 * (resources/assets/themes/<Theme>/icons/<name>.vue and
 * resources/assets/icons/<name>.vue) and exposes `size` / `color` props.
 * Every SVG asset has its component (tests/frontend/iconComponents.test.js);
 * `node scripts/icons/convert-svg-to-vue.mjs --all --missing` generates the
 * missing ones. There is no `.svg` fallback: an injected SVG appeared only
 * after a request, replacing a differently styled image.
 *
 * Resolution order for a themed icon (default):
 *   1. active/explicit theme   2. Default theme   3. shared icons
 *
 * With `shared`, only the shared `resources/assets/icons` set is used.
 */
import { computed, inject, ssrContextKey } from 'vue';
import { getTenantContext } from '@/composables/useTenant';
import { resolveIcon } from '@tenant-registry';

const DEFAULT_THEME = 'Default';

const props = defineProps({
    /** Registered asset name, optionally including its .svg extension. */
    name: { type: String, required: true },
    /** Force a specific theme instead of the active tenant theme. */
    theme: { type: String, default: null },
    /** Resolve from the shared `resources/assets/icons` set. */
    shared: { type: Boolean, default: false },
    size: { type: [Number, String], default: null },
    color: { type: String, default: 'currentColor' },
});

defineOptions({ inheritAttrs: false });

const activeTheme = computed(() => props.theme || getTenantContext().theme || DEFAULT_THEME);

const iconName = computed(() => props.name.replace(/\.svg$/, ''));
const resolved = computed(() => {
    const icon = resolveIcon(iconName.value, {
        theme: activeTheme.value,
        shared: props.shared,
    });
    return icon?.kind === 'component' ? icon : null;
});

// Report the icons of a server render: the browser resolves their components
// before hydrating (runtime/initialHydration.js), so hydration never waits for
// an icon chunk. Outside SSR there is no rendering context and nothing to do.
if (resolved.value) {
    inject(ssrContextKey, null)?.catalogInitialIcons?.add(JSON.stringify([iconName.value, activeTheme.value, props.shared ? 1 : 0]));
}
</script>

<template>
    <component
        :is="resolved.value"
        v-if="resolved"
        :size="size"
        :color="color"
        v-bind="$attrs"
    />
</template>
